import {
	MESApiService,
	dxtToLocalServerTime,
	type spParameter,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	CampaignBilletRow,
	ProductsHistoryPickerCrew,
	ProductsHistoryPickerProductSize,
	ProductsHistoryPickerProductType,
	ProductsHistoryPickers,
	ProductsHistoryRow,
	StandColumnName,
} from "../models/ProductsHistory";

const DISPLAY_FORMAT = "yyyy-MM-dd HH:mm";

// The two branches return different columns for the same instant: the new
// procedures return raw UTC datetimes, the legacy ones return strings already
// converted to the user's timezone. Both are collapsed here into one local
// display string so nothing downstream has to know which database answered.
const toDisplayTime = (
	utc: string | null | undefined,
	legacyLocalString: string | null | undefined,
): string | null => {
	if (utc) return dxtToLocalServerTime(utc, DISPLAY_FORMAT);
	if (legacyLocalString) return legacyLocalString.slice(0, 16);
	return null;
};

const buildProductName = (
	sizeName: string | null,
	typeName: string | null,
): string =>
	[sizeName?.trim(), typeName?.trim()].filter(Boolean).join(" ").trim();

interface RawProductsHistoryRow {
	CampaignId: number;
	FirstBilletStart?: string | null;
	LastBilletEnd?: string | null;
	FirstBilletStartString?: string | null;
	LastBilletEndString?: string | null;
	ProductTypeId: number | null;
	ProductTypeName: string | null;
	ProductSizeId: number | null;
	ProductSizeName: string | null;
	RecipeId: number | null;
	RecipeName: string | null;
	CorrectPassCount: number;
	CorrectPassRolledTonnage: number;
	WrongPassCount: number;
	WrongPassRolledTonnage: number;
}

export interface GetProductsHistoryParams {
	start: string;
	end: string;
	crewId?: string | null;
	productSizeId?: number | null;
	productTypeId?: number | null;
	signal?: AbortSignal;
}

export const getProductsHistory = async ({
	start,
	end,
	crewId = null,
	productSizeId = null,
	productTypeId = null,
	signal,
}: GetProductsHistoryParams): Promise<ProductsHistoryRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "crewId", value: crewId },
		{ name: "productSizeId", value: productSizeId },
		{ name: "productTypeId", value: productTypeId },
	];

	const spName = "[MILL].[GetProductsHistoryDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching products history");
	}

	const rows = get(resp, "data.tables[0].rows", []) as RawProductsHistoryRow[];

	return rows.map<ProductsHistoryRow>((row) => ({
		CampaignId: row.CampaignId,
		FirstBilletStart: toDisplayTime(
			row.FirstBilletStart,
			row.FirstBilletStartString,
		),
		LastBilletEnd: toDisplayTime(row.LastBilletEnd, row.LastBilletEndString),
		ProductName: buildProductName(row.ProductSizeName, row.ProductTypeName),
		ProductTypeId: row.ProductTypeId,
		ProductTypeName: row.ProductTypeName,
		ProductSizeId: row.ProductSizeId,
		ProductSizeName: row.ProductSizeName,
		RecipeId: row.RecipeId,
		RecipeName: row.RecipeName,
		CorrectPassCount: Number(row.CorrectPassCount ?? 0),
		CorrectPassRolledTonnage: Number(row.CorrectPassRolledTonnage ?? 0),
		WrongPassCount: Number(row.WrongPassCount ?? 0),
		WrongPassRolledTonnage: Number(row.WrongPassRolledTonnage ?? 0),
	}));
};

interface RawCampaignBilletStandRow {
	BilletId: number;
	BilletStart?: string | null;
	BilletEnd?: string | null;
	BilletStartString?: string | null;
	BilletEndString?: string | null;
	StandName: string;
	PassNameConfigured: string | null;
	PassNameSetup: string | null;
	WrongPass: number | boolean;
}

export interface GetCampaignBilletsParams {
	campaignId: number;
	signal?: AbortSignal;
}

export const getCampaignBillets = async ({
	campaignId,
	signal,
}: GetCampaignBilletsParams): Promise<CampaignBilletRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "campaignId", value: campaignId }];

	const spName = "[MILL].[GetCampaignProductsWithWrongPassDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching campaign billets");
	}

	const rows = get(
		resp,
		"data.tables[0].rows",
		[],
	) as RawCampaignBilletStandRow[];

	// The procedure returns one row per billet and stand; the grid shows one row
	// per billet with a column per stand.
	const byBillet = new Map<number, CampaignBilletRow>();

	for (const row of rows) {
		let billet = byBillet.get(row.BilletId);
		if (!billet) {
			billet = {
				BilletId: row.BilletId,
				Start: toDisplayTime(row.BilletStart, row.BilletStartString),
				End: toDisplayTime(row.BilletEnd, row.BilletEndString),
			};
			byBillet.set(row.BilletId, billet);
		}
		billet[row.StandName as StandColumnName] = {
			PassNameConfigured: row.PassNameConfigured,
			PassNameSetup: row.PassNameSetup,
			WrongPass: row.WrongPass === true || Number(row.WrongPass) === 1,
		};
	}

	return [...byBillet.values()].sort((a, b) =>
		(a.Start ?? "").localeCompare(b.Start ?? ""),
	);
};

export interface GetProductsHistoryPickersParams {
	start: string;
	end: string;
	signal?: AbortSignal;
}

export const getProductsHistoryPickers = async ({
	start,
	end,
	signal,
}: GetProductsHistoryPickersParams): Promise<ProductsHistoryPickers> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "onlyActives", value: true },
	];

	const spName = "[MILL].[GetProductsHistoryPickersDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching products history pickers");
	}

	const crews = get(
		resp,
		"data.tables[1].rows",
		[],
	) as ProductsHistoryPickerCrew[];
	const productSizes = get(
		resp,
		"data.tables[2].rows",
		[],
	) as ProductsHistoryPickerProductSize[];
	const productTypes = get(
		resp,
		"data.tables[3].rows",
		[],
	) as ProductsHistoryPickerProductType[];

	return { crews, productSizes, productTypes };
};
